import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import type { PatientBanner, PatientMergeDetail, PatientMergeStatus } from '@wodi/shared';
import {
  Button,
  EmptyState,
  LoadingBlock,
  Notice,
  PageHeader,
  Section,
  SegmentedControl,
  Tag,
  useShortcut,
} from '@wodi/ui';
import { useState } from 'react';

import { decidePatientMerge, patientMergeQuery, patientMergesQuery } from '../api/casualty.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { formatInstant } from '../patients/format.ts';
import { PatientBannerView } from '../patients/patient-banner.tsx';
import { ReasonDialog } from '../patients/reason-dialog.tsx';
import { toasts } from '../toasts.ts';
import styles from './casualty.module.css';
import { errorText } from './register-unknown-dialog.tsx';

// Record merges (docs/plans/phase2-casualty.md §5; ADR 0024): an unknown patient's record
// merged into the identified patient's, asked for by one person and decided by another.

const STATUS_WORDS: Record<PatientMergeStatus, string> = {
  requested: 'Waiting',
  approved: 'Approved',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
};

const STATUS_TAG = {
  requested: 'pending',
  approved: 'done',
  rejected: 'failed',
  withdrawn: 'neutral',
} as const;

const CONFLICT_WORDS = {
  open_visit:
    'Both records have an open visit of the same kind here. Close or cancel one of them first.',
  admission: 'Both records have a patient admitted. Discharge or cancel one admission first.',
} as const;

export function PatientMergesPage() {
  const [status, setStatus] = useState<PatientMergeStatus>('requested');
  const list = useQuery({ ...patientMergesQuery(status), refetchInterval: 15_000 });
  return (
    <div className={styles.page}>
      <PageHeader
        title="Record merges"
        description="Unknown patients' records waiting to be merged into the identified patient's."
      />
      <SegmentedControl
        label="Show:"
        value={status}
        onChange={(id) => {
          setStatus(id as PatientMergeStatus);
        }}
        options={(['requested', 'approved', 'rejected', 'withdrawn'] as const).map((s) => ({
          id: s,
          label: STATUS_WORDS[s],
        }))}
      />
      <Section title={STATUS_WORDS[status]}>
        {list.isPending ? <LoadingBlock label="Loading merges" rows={3} /> : null}
        {list.isError ? (
          <Notice
            tone="error"
            action={
              <Button dense onPress={() => void list.refetch()}>
                Try again
              </Button>
            }
          >
            {errorText(list.error, "Merges couldn't be loaded. Try again.")}
          </Notice>
        ) : null}
        {list.isSuccess && list.data.items.length === 0 ? (
          <EmptyState>
            {status === 'requested' ? 'Nothing is waiting for approval.' : 'None yet.'}
          </EmptyState>
        ) : null}
        <ul className={styles.requests}>
          {(list.data?.items ?? []).map((r) => (
            <li key={r.id} className={styles.request}>
              <Link to="/patient-merges/$requestId" params={{ requestId: r.id }}>
                <span className={styles.label}>{r.source.temporaryLabel}</span>
                {` into ${r.target.displayName}`}
              </Link>
              <span className={styles.meta}>
                {`Asked by ${r.requestedBy.displayName}, ${formatInstant(r.requestedAt)}`}
              </span>
            </li>
          ))}
        </ul>
      </Section>
    </div>
  );
}

function Side({ title, banner }: { title: string; banner: PatientBanner }) {
  return (
    <div>
      <h2 className={styles.sideTitle}>{title}</h2>
      <PatientBannerView banner={banner} linked />
    </div>
  );
}

const route = getRouteApi('/_app/patient-merges/$requestId');

export function PatientMergePage() {
  const { requestId } = route.useParams();
  const { data: session } = useSuspenseQuery(sessionQuery);
  const detail = useQuery(patientMergeQuery(requestId));
  const online = useOnline();
  const queryClient = useQueryClient();
  const [rejecting, setRejecting] = useState(false);
  const decide = useMutation({
    mutationFn: ({ action, note }: { action: 'approve' | 'reject' | 'withdraw'; note?: string }) =>
      decidePatientMerge(requestId, action, note),
    onSuccess: async (result: PatientMergeDetail, { action }) => {
      setRejecting(false);
      queryClient.setQueryData(patientMergeQuery(requestId).queryKey, result);
      await queryClient.invalidateQueries({ queryKey: ['patient-merges'] });
      await queryClient.invalidateQueries({ queryKey: ['casualty'] });
      await queryClient.invalidateQueries({ queryKey: ['patients'] });
      toasts.show({
        title:
          action === 'approve'
            ? 'Records merged'
            : action === 'reject'
              ? 'Merge rejected'
              : 'Merge withdrawn',
      });
    },
  });
  const mayApprove = session !== null && can(session, 'patients.merge.approve');
  const isRequester = session !== null && detail.data?.requestedBy.id === session.user.id;
  const waiting = detail.data?.status === 'requested';
  const canApprove =
    mayApprove && !isRequester && waiting && (detail.data?.conflicts.length ?? 1) === 0;
  useShortcut(
    'mod+enter',
    () => {
      if (canApprove && online && !decide.isPending) decide.mutate({ action: 'approve' });
    },
    { enabled: canApprove, description: 'Approve merge' },
  );

  if (detail.isPending) return <LoadingBlock label="Loading the merge" />;
  if (detail.isError) {
    return (
      <Notice
        tone="error"
        action={<Button onPress={() => void detail.refetch()}>Try again</Button>}
      >
        {errorText(detail.error, "The merge couldn't be loaded. Try again.")}
      </Notice>
    );
  }
  const d = detail.data;
  return (
    <div className={styles.page}>
      <PageHeader
        title={`Merge ${d.source.temporaryLabel}`}
        description={`Into ${d.target.displayName}, patient ${d.target.patientNumber}.`}
        actions={<Tag state={STATUS_TAG[d.status]}>{STATUS_WORDS[d.status]}</Tag>}
      />
      <div className={styles.side}>
        <Side title="Unknown record" banner={d.sourceBanner} />
        <Side title="Identified patient" banner={d.targetBanner} />
      </div>
      <Section title="Why these are the same person">
        <p>{d.reason}</p>
        <p className={styles.meta}>
          {`Asked by ${d.requestedBy.displayName}, ${formatInstant(d.requestedAt)}.`}
          {d.decidedBy === null || d.decidedAt === null
            ? ''
            : ` ${STATUS_WORDS[d.status]} by ${d.decidedBy.displayName}, ${formatInstant(d.decidedAt)}.`}
          {d.decisionNote === null ? '' : ` Note: ${d.decisionNote}`}
        </p>
        {d.unknownDescription === null ? null : (
          <p className={styles.meta}>{`Description when registered: ${d.unknownDescription}`}</p>
        )}
      </Section>
      <Section title="What the unknown record holds">
        <ul className={styles.facts}>
          {d.contents.visits.map((v) => (
            <li key={v.id}>
              {`Visit `}
              <span className={styles.label}>{v.visitNumber}</span>
              {`, ${v.status}, from ${formatInstant(v.startedAt)}`}
            </li>
          ))}
          <li>{`${String(d.contents.allergies)} allergies recorded`}</li>
          <li>{`${String(d.contents.diagnoses)} diagnoses`}</li>
          <li>{`${String(d.contents.labOrders)} lab orders`}</li>
          <li>{`${String(d.contents.prescriptions)} prescriptions`}</li>
        </ul>
        <p className={styles.meta}>
          Nothing is moved or changed. After approval, all of this shows under the identified
          patient, and the safety checks use it.
        </p>
      </Section>
      {waiting && d.conflicts.length > 0 ? (
        <Notice tone="info">{d.conflicts.map((c) => CONFLICT_WORDS[c]).join(' ')}</Notice>
      ) : null}
      {waiting && isRequester ? (
        <Notice tone="info">
          You asked for this merge, so someone else with approval rights must decide it.
        </Notice>
      ) : null}
      {decide.error === null ? null : (
        <Notice tone="error">{errorText(decide.error, 'That did not work. Try again.')}</Notice>
      )}
      {waiting ? (
        <div className={styles.actions} role="toolbar" aria-label="Merge actions">
          {mayApprove && !isRequester ? (
            <>
              <Button
                variant="primary"
                isDisabled={!online || !canApprove}
                isPending={decide.isPending}
                pendingLabel="Merging…"
                onPress={() => {
                  decide.mutate({ action: 'approve' });
                }}
              >
                Approve merge
              </Button>
              <Button
                isDisabled={!online}
                onPress={() => {
                  setRejecting(true);
                }}
              >
                Reject
              </Button>
            </>
          ) : null}
          {isRequester ? (
            <Button
              variant="quiet"
              isDisabled={!online}
              onPress={() => {
                decide.mutate({ action: 'withdraw' });
              }}
            >
              Withdraw request
            </Button>
          ) : null}
        </div>
      ) : null}
      <ReasonDialog
        title="Reject merge"
        isOpen={rejecting}
        onOpenChange={setRejecting}
        confirmLabel="Reject"
        pendingLabel="Rejecting…"
        isPending={decide.isPending}
        error={decide.error === null ? null : errorText(decide.error, 'That did not work.')}
        onConfirm={(note) => {
          decide.mutate({ action: 'reject', note });
        }}
      >
        <p>Say why these are not the same person. The unknown record stays as it is.</p>
      </ReasonDialog>
    </div>
  );
}
